import { useEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@/store';
import { 
  usePostPayBalanceMutation
} from '@/store/api';
import { 
  selectAmountToPay, 
  selectSelectedPaymentMethod 
} from '@/store/selectors/paymentsSelectors';
import { setPayingWithSourceId } from '@/store/slices/residents/paymentsSlice';
import { useToast } from '@/contexts/admin/ToastContext';

type ProcessingCallbacks = {
  onSuccess: (args: { leaseId: number; residentId?: number; amount: number; paymentMethod?: any }) => void;
  onFailure: () => void;
  onTimeout: () => void;
};

type ProcessingParams = {
  leaseId: number;
  residentId?: number;
  amount?: number;
  sourceId?: number;
  paymentId?: string;
} & ProcessingCallbacks;

export const usePaymentProcessing = ({
  leaseId,
  residentId,
  amount,
  sourceId,
  paymentId,
  onSuccess,
  onFailure,
  onTimeout,
}: ProcessingParams) => {
  const dispatch = useAppDispatch();
  const { showError, showSuccess } = useToast();

  // Stabilize external callbacks with refs to avoid re-run loops in effects
  const onSuccessRef = useRef(onSuccess);
  const onFailureRef = useRef(onFailure);
  const onTimeoutRef = useRef(onTimeout);
  const showErrorRef = useRef(showError);
  const showSuccessRef = useRef(showSuccess);

  useEffect(() => { onSuccessRef.current = onSuccess; }, [onSuccess]);
  useEffect(() => { onFailureRef.current = onFailure; }, [onFailure]);
  useEffect(() => { onTimeoutRef.current = onTimeout; }, [onTimeout]);
  useEffect(() => { showErrorRef.current = showError; }, [showError]);
  useEffect(() => { showSuccessRef.current = showSuccess; }, [showSuccess]);

  // Store fallbacks
  const storeAmountToPay = useAppSelector(selectAmountToPay);
  const storeSelectedPaymentMethod = useAppSelector(selectSelectedPaymentMethod);
  const authUser = useAppSelector((s) => (s as any).auth?.user);

  // Progress messaging
  const messages = useMemo(() => [
    'Transaction details are sending. Please wait. This should only take a few moments.',
    'This process is taking longer than expected. We are working on it.',
    'This is taking a really long time. While this is frustrating, we are actively working on resolving the issue. Please check back in 15 mins and this should be processed.',
  ], []);
  const totalSteps = messages.length;
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null);

  // Payment API
  const [postPayBalance, { isLoading, isSuccess, isError, error }] = usePostPayBalanceMutation();

  // Kick off processing on mount
  useEffect(() => {
    const processPayment = async () => {
      const paymentAmount = amount || storeAmountToPay;
      const paymentSourceId = sourceId || storeSelectedPaymentMethod?.sourceId || storeSelectedPaymentMethod?.id;
      const paymentResidentId = residentId || authUser?.id;

      if (!paymentAmount || !paymentSourceId || !leaseId) {
        showErrorRef.current?.('Missing payment information. Please try again.');
        onFailureRef.current?.();
        return;
      }

      try {
        await postPayBalance({
          amount: paymentAmount,
          leaseId,
          sourceId: Number(paymentSourceId) || 0,
          residentId: paymentResidentId,
        }).unwrap();
      } catch (_err) {
        // Error handled in downstream effect
      }
    };

    processPayment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle message progression and timeout
  useEffect(() => {
    if (isLoading) {
      const timer = setTimeout(() => {
        if (currentMessageIndex < messages.length - 1) {
          setCurrentMessageIndex((prev) => prev + 1);
        } else {
          onTimeoutRef.current?.();
        }
      }, 10000);
      setTimeoutId(timer);
      return () => {
        if (timer) clearTimeout(timer);
      };
    }
  }, [isLoading, currentMessageIndex, messages.length]);

  // Handle success/failure
  useEffect(() => {
    if (isSuccess) {
      if (timeoutId) clearTimeout(timeoutId);
      showSuccessRef.current?.('Payment received. A confirmation has been sent to your email.');
      onSuccessRef.current?.({
        leaseId,
        residentId,
        amount: amount || storeAmountToPay,
        paymentMethod: storeSelectedPaymentMethod,
      });
    }

    if (isError) {
      if (timeoutId) clearTimeout(timeoutId);
      dispatch(setPayingWithSourceId(0));

      const errorMessage = (error as any)?.data?.error?.message;
      const isEntrataError = errorMessage?.includes('Entrata request') || errorMessage?.includes('getMitsLeaseArTransactions');
      const message = isEntrataError
        ? 'Payment processing is temporarily unavailable due to system maintenance. Please try again later or contact support.'
        : 'There was an error processing your payment. Please try again.';
      showErrorRef.current?.(message);
      onFailureRef.current?.();
    }
  }, [
    isSuccess,
    isError,
    timeoutId,
    dispatch,
    leaseId,
    residentId,
    amount,
    storeAmountToPay,
    storeSelectedPaymentMethod,
    error,
  ]);

  const progressPercent = ((currentMessageIndex + 1) / totalSteps) * 100;

  return {
    isLoading,
    isSuccess,
    isError,
    message: messages[currentMessageIndex],
    currentMessageIndex,
    totalSteps,
    progressPercent,
  };
};


